import React from 'react';
import { Download, Settings, Gamepad2 } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const iconMap = {
  download: Download,
  settings: Settings,
  'gamepad-2': Gamepad2
};

const HeroSection = () => {
  const { t } = useLanguage();

  const processSteps = [
    {
      id: 1,
      step: t('hero.step1'),
      title: t('hero.step1Title'),
      description: t('hero.step1Desc'),
      icon: "download"
    },
    {
      id: 2,
      step: t('hero.step2'),
      title: t('hero.step2Title'),
      description: t('hero.step2Desc'),
      icon: "settings"
    },
    {
      id: 3,
      step: t('hero.step3'),
      title: t('hero.step3Title'),
      description: t('hero.step3Desc'),
      icon: "gamepad-2"
    }
  ];

  return (
    <section className="min-h-screen pt-8 md:pt-24 pb-12 md:pb-16 px-4 md:px-8 overflow-x-hidden">
      {/* Header */}
      <AnimateOnScroll animation="fade-down" className="text-center mb-10 md:mb-16 w-full">
        <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-4 md:mb-6">
          {t('hero.badge')}
        </span>
        <h1 className="text-xl sm:text-2xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4 px-2">
          {t('hero.title')} <br className="sm:hidden" /><span className="text-cyan-500">{t('hero.titleHighlight')}</span>
        </h1>
        <p className="text-gray-400 text-xs sm:text-sm md:text-lg max-w-2xl mx-auto px-4">
          {t('hero.subtitle')}
        </p>
      </AnimateOnScroll>

      {/* Process Steps */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {processSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <AnimateOnScroll 
                key={step.id} 
                animation="fade-up" 
                delay={index * 80}
                className="relative"
              >
                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-20 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-cyan-600 to-transparent"></div>
                )}
                
                {/* Card */}
                <div className="relative bg-zinc-900/50 border border-cyan-900/30 rounded-xl md:rounded-2xl p-5 md:p-8 hover:border-cyan-600/50 transition-all duration-300 group card-hover">
                  {/* Icon */}
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl bg-cyan-950/50 border border-cyan-800/50 flex items-center justify-center mb-4 md:mb-6 group-hover:bg-cyan-600 transition-all duration-300">
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-cyan-500 group-hover:text-white transition-colors" />
                  </div>
                  
                  {/* Content */}
                  <span className="inline-block text-[10px] md:text-xs font-semibold text-cyan-500 uppercase tracking-wider mb-2 md:mb-3">
                    {step.step}
                  </span>
                  <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
